import React, { useEffect } from "react";
import {
  SITE_NAME,
  POLICY_UPDATED,
  CONTACT_URL,
  CONTACT_LABEL,
} from "../constants/siteConfig";

export default function TermsAndConditions({ onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="legal-backdrop" onClick={onClose}>
      <article
        className="legal-modal"
        role="dialog"
        aria-label="Terms and Conditions"
        onClick={(e) => e.stopPropagation()}
      >
        <header>
          <h2>Terms and Conditions</h2>
          <button
            className="slide-panel-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="legal-body">
          <p className="legal-updated">
            Last updated: {POLICY_UPDATED}
          </p>

          <p>
            Welcome to {SITE_NAME}. These Terms and Conditions ("Terms")
            govern your use of the {SITE_NAME} website, map, database, accounts,
            submissions, and related services. By accessing or using the site,
            you agree to these Terms. If you do not agree with these Terms,
            please do not use the site.
          </p>

          <h3>1. About {SITE_NAME}</h3>

          <p>
            {SITE_NAME} is a community map and open-data project focused on
            documenting and sharing information about shipwrecks. The site
            allows users to browse publicly available information and, where
            permitted, create accounts and contribute information, edits,
            sources, photographs, and other content.
          </p>

          <p>
            {SITE_NAME} is a community-driven project. Information submitted by
            users may not always be complete, accurate, current, or independently
            verified.
          </p>

          <h3>2. Accounts</h3>

          <p>
            Some features require an account. When creating an account, you
            agree to provide information that is reasonably accurate and to
            keep your account information up to date.
          </p>

          <p>
            You are responsible for maintaining the security of your password
            and account. You should not share your password or knowingly allow
            another person to use your account.
          </p>

          <p>
            You are responsible for activity performed through your account
            unless you can demonstrate that the activity occurred without your
            authorization and that you took reasonable steps to protect your
            account.
          </p>

          <h3>3. User Submissions</h3>

          <p>
            {SITE_NAME} allows users to submit information about shipwrecks,
            including names, locations, historical information, descriptions,
            sources, links, photographs, photo URLs, attribution information,
            and proposed edits.
          </p>

          <p>
            By submitting content, you represent that you have the right to
            submit that content and that your submission does not knowingly
            violate another person's rights or applicable law.
          </p>

          <p>
            You should make reasonable efforts to ensure that information you
            submit is accurate and appropriately sourced. Do not knowingly
            submit false, misleading, fraudulent, or malicious information.
          </p>

          <h3>4. Photographs and Image Rights</h3>

          <p>
            If you submit a photograph or image, you must have the necessary
            rights or permission to submit it. You should provide accurate
            photographer, author, copyright, source, and license information
            when available.
          </p>

          <p>
            <strong>
              If you are the author, photographer, copyright holder, or rights
              holder of an image displayed on {SITE_NAME} and want the image
              removed, please contact us and request its removal.
            </strong>
          </p>

          <p>
            An image may have been submitted by a user who is not the original
            photographer or copyright holder. Contacting {SITE_NAME} is an
            available way to request removal of an image regardless of who
            originally submitted it.
          </p>

          <p>
            We may request information reasonably necessary to evaluate an
            image-removal or copyright request, such as the image URL, original
            source, proof of authorship or ownership, or authorization to act
            on behalf of the rights holder.
          </p>

          <h3>5. License to Display Submissions</h3>

          <p>
            By submitting content to {SITE_NAME}, you grant {SITE_NAME} a
            non-exclusive, worldwide, royalty-free license to host, store,
            reproduce, display, and distribute that submission as necessary to
            operate and promote the site and its public database.
          </p>

          <p>
            This license does not transfer ownership of your original
            copyright or other intellectual property rights to {SITE_NAME}.
          </p>

          <p>
            If your submission includes content owned by someone else, you are
            responsible for ensuring that you have permission to grant the
            rights necessary for us to display that content.
          </p>

          <p>
            We may remove or restrict submitted content when necessary to
            address copyright concerns, legal requirements, abuse, moderation
            decisions, technical issues, or other legitimate reasons.
          </p>

          <h3>6. Public Contributions</h3>

          <p>
            Approved shipwreck submissions and edits may become publicly
            available through the site. This may include shipwreck names,
            coordinates, descriptions, historical information, links,
            photographs, photo attribution, photo credit/license information,
            and contributor usernames.
          </p>

          <p>
            Do not submit private, confidential, sensitive, or personally
            identifying information about yourself or another person unless it
            is necessary, appropriate, and lawful to make that information
            public.
          </p>

          <h3>7. Public Data and Exports</h3>

          <p>
            {SITE_NAME} may provide public data exports, including CSV and
            GeoJSON files containing approved shipwreck information.
          </p>

          <p>
            Public exports are intended to make the site's public shipwreck
            information accessible and reusable. Photos and photo-credit
            metadata are excluded from the site's public CSV and GeoJSON
            exports.
          </p>

          <p>
            Users who download or reuse public data are responsible for
            complying with applicable laws, licenses, attribution requirements,
            and the rights of third parties.
          </p>

          <h3>8. Content Moderation</h3>

          <p>
            Submissions may be reviewed by administrators or moderators before
            being published. {SITE_NAME} may approve, reject, modify, hide, or
            remove submissions or edits at its discretion.
          </p>

          <p>
            Administrators may make reasonable corrections or edits to pending
            submissions when necessary to improve accuracy, formatting,
            completeness, attribution, or compliance with these Terms.
          </p>

          <p>
            Administrators may also deny submissions or edits and may provide a
            reason for the decision where appropriate.
          </p>

          <p>
            Moderation decisions are made to protect the quality, reliability,
            and usefulness of the database. A submission being approved does
            not necessarily mean that {SITE_NAME} independently verified every
            detail contained in it.
          </p>

          <h3>9. Prohibited Uses</h3>

          <p>
            You agree not to use {SITE_NAME} to:
          </p>

          <ul>
            <li>
              Knowingly submit false, fraudulent, or intentionally misleading
              information.
            </li>
            <li>
              Upload or submit content that you do not have the right to use.
            </li>
            <li>
              Infringe copyrights, trademarks, privacy rights, or other rights
              of another person or organization.
            </li>
            <li>
              Harass, threaten, impersonate, or target another user or person.
            </li>
            <li>
              Attempt to gain unauthorized access to accounts, systems, or
              databases.
            </li>
            <li>
              Circumvent security measures, rate limits, or access controls.
            </li>
            <li>
              Use automated methods to abuse, overload, scrape, or interfere
              with the service in a manner that harms its operation or violates
              applicable rules or laws.
            </li>
            <li>
              Upload malware, malicious code, or other harmful material.
            </li>
            <li>
              Use the service for unlawful purposes.
            </li>
          </ul>

          <h3>10. Shipwreck Locations and Safety</h3>

          <p>
            Information displayed on {SITE_NAME}, including coordinates and
            descriptions of shipwrecks, is provided for informational and
            research purposes.
          </p>

          <p>
            You are responsible for determining whether it is safe, lawful, and
            appropriate to travel to, dive at, visit, or otherwise interact
            with a location identified on the site.
          </p>

          <p>
            Shipwreck locations may be underwater, on private property, in
            restricted areas, in protected areas, or in locations subject to
            local, state, federal, or international laws and regulations.
          </p>

          <p>
            {SITE_NAME} does not guarantee that any listed location is
            accessible, safe, legal to visit, or accurately represented.
          </p>

          <h3>11. Accuracy of Information</h3>

          <p>
            {SITE_NAME} is a community-contributed database. Information may
            contain errors, outdated information, approximate coordinates,
            conflicting historical accounts, incorrect identifications, or
            incomplete records.
          </p>

          <p>
            We do not guarantee that information on the site is accurate,
            complete, current, reliable, or suitable for a particular purpose.
          </p>

          <p>
            If you identify an error, you are encouraged to submit a correction
            or contact the site administrators.
          </p>

          <h3>12. Intellectual Property</h3>

          <p>
            The {SITE_NAME} software, site design, logos, branding, and other
            original materials may be protected by copyright, trademark, or
            other intellectual property laws.
          </p>

          <p>
            User-submitted content may be owned by the users or third parties
            who created or supplied that content. Nothing in these Terms
            automatically transfers ownership of third-party photographs,
            writings, trademarks, or other copyrighted material to{" "}
            {SITE_NAME}.
          </p>

          <p>
            Where content is provided under a specific license, the applicable
            license and its terms may control how that content can be reused.
          </p>

          <h3>13. Account Deletion</h3>

          <p>
            You may delete your account through the available account settings
            when that functionality is provided.
          </p>

          <p>
            Deleting an account does not necessarily delete contributions that
            you previously submitted. Shipwreck records, edits, moderation
            history, and other public database information may be retained to
            preserve the history and integrity of the project.
          </p>

          <p>
            Where applicable, the username associated with retained
            contributions may be replaced with <strong>"deleted"</strong>.
          </p>

          <p>
            If you believe specific content should be removed after deleting
            your account, you may contact us and explain your request.
          </p>

          <h3>14. Disabled Accounts</h3>

          <p>
            {SITE_NAME} may disable or restrict accounts that violate these
            Terms, abuse the service, engage in suspicious activity, or
            otherwise threaten the security or integrity of the project.
          </p>

          <p>
            A disabled account may no longer be able to sign in or use certain
            account features. Disabling an account does not automatically
            remove previously submitted contributions.
          </p>

          <h3>15. Availability of the Service</h3>

          <p>
            We may modify, suspend, restrict, or discontinue any part of
            {SITE_NAME} at any time. Features may change, become unavailable,
            or be removed without notice.
          </p>

          <p>
            We do not guarantee that the site will always be available,
            uninterrupted, error-free, or compatible with every device or
            browser.
          </p>

          <h3>16. Third-Party Services and Links</h3>

          <p>
            {SITE_NAME} may use or link to third-party services, websites, map
            providers, image hosts, and other resources.
          </p>

          <p>
            We are not responsible for the availability, accuracy, security,
            privacy practices, content, or terms of third-party services.
            Visiting or using a third-party service is subject to that
            service's own terms and policies.
          </p>

          <h3>17. Disclaimer of Warranties</h3>

          <p>
            To the extent permitted by applicable law, {SITE_NAME} and its
            operators provide the site and its content on an "as is" and "as
            available" basis without warranties of any kind, whether express
            or implied.
          </p>

          <p>
            We do not guarantee the accuracy, completeness, reliability,
            availability, safety, legality, or fitness for a particular purpose
            of information provided through the site.
          </p>

          <h3>18. Limitation of Liability</h3>

          <p>
            To the maximum extent permitted by applicable law, {SITE_NAME} and
            its operators will not be liable for damages or losses arising from
            your use of, or inability to use, the site or information obtained
            through the site.
          </p>

          <p>
            This includes, to the extent permitted by law, losses resulting
            from inaccurate information, unavailable services, user-submitted
            content, third-party services, or decisions made based on
            information found on {SITE_NAME}.
          </p>

          <h3>19. Indemnification</h3>

          <p>
            To the extent permitted by applicable law, you agree to be
            responsible for claims, damages, losses, liabilities, and reasonable
            expenses arising from your violation of these Terms, your unlawful
            use of the site, or content that you submit to {SITE_NAME}.
          </p>

          <h3>20. Changes to These Terms</h3>

          <p>
            We may update these Terms from time to time as the site changes or
            as necessary to address legal, security, operational, or community
            requirements.
          </p>

          <p>
            When we make changes, we will update the "Last updated" date above.
            Your continued use of {SITE_NAME} after updated Terms are posted
            means that the updated Terms will apply to your use of the site
            going forward.
          </p>

          <h3>21. Contact</h3>

          <p>
            Questions about these Terms, content concerns, copyright issues,
            image removal requests, or other legal or site-related matters can
            be sent through{" "}
            <a
              href={CONTACT_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {CONTACT_LABEL}
            </a>
            .
          </p>
        </div>

        <footer>
          <button className="panel-primary" onClick={onClose}>
            Close
          </button>
        </footer>
      </article>
    </div>
  );
}
